import { useEffect, useRef } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import type { SubmitHandler } from "react-hook-form";
import { Controller, useForm } from "react-hook-form";

import type { TwoFactorAuthFormValuesType } from "@/types";
import { twoFactorAuthFormSchema } from "@/types";
import {
  Button,
  FormField,
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
  Spinner,
} from "@/ui";

interface QrCodeProps {
  isLoadingChallengeCode: boolean;
  isLoadingQr?: boolean;
  hasTwoFaError: boolean;
  qrCode?: string;
  onSubmit: SubmitHandler<{
    code: string;
  }>;
  handleShowRecoveryCode?: () => void;
}
export const TwoFactorCodeChallenge = ({
  isLoadingChallengeCode,
  qrCode,
  onSubmit,
  hasTwoFaError,
  handleShowRecoveryCode,
  isLoadingQr,
}: QrCodeProps) => {
  const { control, handleSubmit } = useForm<TwoFactorAuthFormValuesType>({
    resolver: zodResolver(twoFactorAuthFormSchema),
  });

  const otpRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    otpRef.current?.focus();
  }, []);

  return (
    <>
      <div className="flex flex-col gap-y-2 text-center text-primary-950">
        <h1 className="text-2xl font-bold">
          {qrCode ? "Enable two factor authentication" : "Enter 2FA code"}
        </h1>
        {!qrCode && (
          <p>
            Go to your authenticator app, then enter the generated code below.
          </p>
        )}
        {qrCode && (
          <p>
            For extra security, scan this QR with your authenticator app, then
            enter the generated code below.
          </p>
        )}
      </div>
      {isLoadingQr && <Spinner />}
      {qrCode && (
        <img src={qrCode} alt="QR Code" className="bg-black m-auto size-52" />
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-10">
        <FormField label="">
          <Controller
            name="code"
            control={control}
            render={({ field }) => (
              <InputOTP
                maxLength={6}
                value={field.value}
                onChange={field.onChange}
                ref={otpRef}
                disabled={isLoadingChallengeCode}
              >
                <InputOTPGroup>
                  {Array.from({ length: 6 }, (_, index) => (
                    <InputOTPSlot
                      key={index}
                      index={index}
                      hasError={hasTwoFaError}
                    />
                  ))}
                </InputOTPGroup>
              </InputOTP>
            )}
          />
          {hasTwoFaError && (
            <p className="text-center text-error-700">
              Invalid code, please try again.
            </p>
          )}
        </FormField>
        <div className="flex flex-col gap-y-6">
          {handleShowRecoveryCode && (
            <button
              type="button"
              onClick={handleShowRecoveryCode}
              className="self-center border-b text-sm font-bold text-primary-950"
            >
              Use recovery codes
            </button>
          )}
          <Button
            className="w-full font-semibold"
            type="submit"
            isLoading={isLoadingChallengeCode}
            disabled={isLoadingChallengeCode}
          >
            Verify and continue
          </Button>
        </div>
      </form>
    </>
  );
};
